import { CacheControl } from '@/lib/s3';
import {
  AttachmentType,
  IAttachmentCategory,
} from '@/redux/attachment/attachment.d';
import {
  IWood,
  IMetals,
  IPlastic,
  IOther,
  ILicense,
  IDifficulty,
  ILaserOrEndmill,
  IJobType,
  IFlipDirection,
  IZZeroPosition,
  IXYDatumPosition,
  IUnits,
  IToolSetter,
  IProbeForZHeightCommand,
  ISoftwareUsed,
} from '@/redux/post/post.d';
import type { GcodeExtractedData } from '@/workers/gcodeParser';
import {
  V2Basics,
  V2Bit,
  V2File,
  V2JobDetails,
  V2Operation,
} from '../v2Upload.d';
import { stripControllerPrefix, validSpindle } from './fileHelpers';

/** True when `value` is a member of a string enum's value set. */
function enumHas<T extends Record<string, string>>(
  enumObj: T,
  value: string | null | undefined
): value is T[keyof T] {
  return !!value && (Object.values(enumObj) as string[]).includes(value);
}

/** All backend material enum values flattened into one set, for validating
 * BasicsStep's material picker before it's sent as materialUsed. */
const ALL_MATERIALS: Record<string, string> = {
  ...IWood,
  ...IMetals,
  ...IPlastic,
  ...IOther,
};

/** LicenseSelector stores short CC codes; the backend's ILicense enum uses
 * full license names. Maps 1:1 across all 7 licenses. */
const CC_CODE_TO_LICENSE: Record<string, ILicense> = {
  CC0: ILicense.CreativeCommonsPublicDomain,
  'CC BY': ILicense.CreativeCommonsAttribution,
  'CC BY-SA': ILicense.CreativeCommonsAttributionShareAlike,
  'CC BY-ND': ILicense.CreativeCommonsAttributionNoDerivatives,
  'CC BY-NC': ILicense.CreativeCommonsAttributionNoncommercial,
  'CC BY-NC-SA': ILicense.CreativeCommonsAttributionNoncommercialShareAlike,
  'CC BY-NC-ND': ILicense.CreativeCommonsAttributionNoncommercialNoDerivatives,
};

/** The read side of CC_CODE_TO_LICENSE: a resumed post carries the backend's
 * full license name, the LicenseSelector only understands the CC code. */
export const LICENSE_TO_CC_CODE: Record<string, string> = Object.fromEntries(
  Object.entries(CC_CODE_TO_LICENSE).map(([code, license]) => [license, code])
);

/** Prefer the parser-extracted value; fall back to the user-typed one; only
 * ever return a value that's actually a member of the enum, so a stray/free
 * -text value can never sneak into the update payload and 400 it. */
function pickValidEnumValue<T extends Record<string, string>>(
  enumObj: T,
  parsed: string | null | undefined,
  asked: string
): T[keyof T] | undefined {
  if (enumHas(enumObj, parsed)) return parsed;
  if (enumHas(enumObj, asked)) return asked;
  return undefined;
}

/** A post patch is any subset of the post's fields. updatePost's validator
 * takes them all as optional, so each builder below emits only the keys it
 * could actually validate. */
type PostPatch = Record<string, unknown>;

/** The two fields the user answers on Sign off when the parser couldn't read
 * them out of the G-code. Empty when nobody has been asked yet. */
interface ParserAnswers {
  jobType?: string;
  zZeroPosition?: string;
}

/** Drop keys whose value is null/undefined so a patch carries only what's set. */
function compact(obj: Record<string, unknown>): PostPatch {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v != null));
}

/** The value if it's a real member of the enum, else undefined. */
function pickEnum<T extends Record<string, string>>(
  enumObj: T,
  value: string | null | undefined
): T[keyof T] | undefined {
  return enumHas(enumObj, value) ? value : undefined;
}

/** The same fields as numbers, or `undefined` unless every one is finite and
 * above zero — Joi's `.positive()` on each member is the reason for the guard.
 * All-or-nothing so `compact` drops a half-filled object Joi would reject. */
function positiveDims(
  fields: Record<string, string>
): Record<string, number> | undefined {
  const entries = Object.entries(fields).map(
    ([key, raw]) => [key, Number(raw)] as const
  );
  return entries.every(([, n]) => Number.isFinite(n) && n > 0)
    ? Object.fromEntries(entries)
    : undefined;
}

/**
 * The `jobSize` shape `createPostDto` expects for this job type.
 *
 * It is a `Joi.alternatives().conditional('jobType', …)`: ROTARY takes
 * `{length, diameter}` and every other type takes `{width, height, thickness}`.
 * Sending the wrong shape — or, as this did before rotary had any inputs at all,
 * sending nothing — fails the completeness gate that guards `publish: true`, so
 * the submit 400s with "the project is incomplete" and names no field.
 *
 * `undefined` when the numbers are not all present and positive, so `compact`
 * drops the key rather than sending a half-filled object Joi would reject.
 */
function jobSizeFor(
  jobType: string | undefined,
  jd: V2JobDetails
): Record<string, number> | undefined {
  if (!jobType) return undefined;
  if (jobType === IJobType.ROTARY) {
    return positiveDims({ length: jd.length, diameter: jd.diameter });
  }
  return positiveDims({
    width: jd.width,
    height: jd.height,
    thickness: jd.thickness,
  });
}

/** The always-sent job-setup fields derived from the toggles. The ATC answer
 * is stored on probeForZHeightCommand (v1's "Automatic Tool Changer" field);
 * material links only ride along when the dimensional-material toggle is on. */
function jobToggles(jd: V2JobDetails) {
  return {
    toolSetter: jd.toolSetter ? IToolSetter.ENABLED : IToolSetter.DISABLED,
    probeForZHeightCommand: jd.autoToolChanger
      ? IProbeForZHeightCommand.YES
      : IProbeForZHeightCommand.NO,
    isDimensionalMaterial: jd.isDimensionalMaterial,
    dimensionalMaterialLinks: jd.isDimensionalMaterial
      ? jd.dimensionalMaterialLinks.filter((l) => l.trim())
      : [],
  };
}

/**
 * The bounding box the parser measured, for the job types it describes.
 *
 * A bounding box IS the non-rotary shape by definition — there is nothing in a
 * G-code file that says "this cylinder is 3 inches across". A rotary job's
 * length/diameter is the user's to enter on Job details, so nothing is seeded
 * for it.
 */
function parsedJobSize(
  jobType: string | undefined,
  dimensions: GcodeExtractedData['dimensions'] | undefined
) {
  if (!jobType || jobType === IJobType.ROTARY) return undefined;
  return dimensions;
}

/** Everything the G-code parser knows about the job, guarded so only real enum
 * members are sent — an unguarded free-text value would 400 the whole update.
 * Shared by the Files-step sync (which has no answers yet) and Sign off (which
 * has the user's). */
export function buildParserPatch(
  extractedData: GcodeExtractedData | null,
  answers: ParserAnswers = {}
): PostPatch {
  const ex: Partial<GcodeExtractedData> = extractedData ?? {};
  const jobType = pickValidEnumValue(
    IJobType,
    ex.jobType,
    answers.jobType ?? ''
  );
  const zZeroPosition = pickValidEnumValue(
    IZZeroPosition,
    ex.zZeroPosition,
    answers.zZeroPosition ?? ''
  );
  return compact({
    units: pickEnum(IUnits, ex.units),
    jobType,
    zZeroPosition,
    xyDatumPosition: pickEnum(IXYDatumPosition, ex.xyDatumPosition),
    jobSize: parsedJobSize(jobType, ex.dimensions),
  });
}

/** The Job details step's editable job setup, guarded the same way as
 * buildParserPatch. softwareUsed is filtered against the fixed ISoftwareUsed
 * list so a stale value can never 400 the patch. */
export function buildJobDetailsPatch(jd: V2JobDetails): PostPatch {
  const jobType = pickEnum(IJobType, jd.jobType);
  const softwareUsed = jd.softwareUsed.filter((s) =>
    (Object.values(ISoftwareUsed) as string[]).includes(s)
  );
  return {
    ...compact({
      units: pickEnum(IUnits, jd.units),
      jobType,
      zZeroPosition: pickEnum(IZZeroPosition, jd.zZeroPosition),
      xyDatumPosition: pickEnum(IXYDatumPosition, jd.xyDatumPosition),
      jobSize: jobSizeFor(jobType, jd),
      softwareUsed: softwareUsed.length ? softwareUsed : undefined,
      // Required by `createPostDto` when — and only when — the job is double
      // sided, and forbidden nowhere, so it is sent only for that case. v2 never
      // sent it at all, which meant no double-sided project could be submitted
      // (8 of the 141 posts on the production account are double sided).
      flipDirection:
        jobType === IJobType.DOUBLE_SIDED
          ? pickEnum(IFlipDirection, jd.flipDirection)
          : undefined,
    }),
    ...jobToggles(jd),
  };
}

/** Difficulty, material and license — collected across Basics, Job details and
 * License. v2's own pickers use a different vocabulary than the backend
 * (free-text materials, CC license codes), so each is dropped unless it maps
 * onto a real enum member. */
export function buildBasicsMetaPatch(basics: V2Basics): PostPatch {
  return compact({
    materialUsed: pickEnum(ALL_MATERIALS, basics.material),
    difficulty: pickEnum(IDifficulty, basics.difficulty),
    licenceType: CC_CODE_TO_LICENSE[basics.license],
  });
}

/** G-code files get the controller prefix so the backend's filename-based
 * controller detection (getControllerFromFileName) keeps working. The
 * controller is only final once the user has placed every G-code file in a
 * controller column, which is why files upload on leaving Add files.
 * restorePost reverses this to get the displayed name back.
 *
 * Stripped first: the name may already carry a prefix — a file downloaded from
 * a post and added back, or a draft file restored and re-uploaded — and the
 * stored name has to hold exactly one, naming the column the file is in NOW. */
export function uploadFileName(file: V2File): string {
  if (file.kind !== 'gcode') return file.name;
  return `${file.controller}_${stripControllerPrefix(file.name).name}`;
}

/** The wizard lets the user rename a file after picking it, but the raw File
 * still carries its original disk name — and multipart uploads key off that.
 * Re-wrap so the name the backend sees matches the one the S3 copy went up
 * with (and stays stable across re-entry, which is what makes the backend's
 * upsert a no-op rather than a delete + re-add).
 *
 * Undefined for a file restored from the server: it has no bytes on the client
 * and is already an attachment on the post, so there is nothing to send. */
export function fileForUpload(file: V2File): File | undefined {
  const raw = file.file;
  if (!raw) return undefined;
  const name = uploadFileName(file);
  if (name === raw.name) return raw;
  return new File([raw], name, { type: raw.type });
}

export function attachmentCategoryFor(
  kind: V2File['kind']
): IAttachmentCategory {
  return kind === 'gcode'
    ? IAttachmentCategory.postModel
    : IAttachmentCategory.postMedia;
}

/** An svg is filed under Vectors in the wizard, but storage and the post
 * carousel both have to see it as an image or it renders as "cannot be
 * displayed". Section and resource type are separate questions. */
export function attachmentResourceTypeFor(file: {
  kind: V2File['kind'];
  name: string;
}): AttachmentType {
  if (file.kind === 'image') return AttachmentType.image;
  if (file.kind === 'gcode') return AttachmentType.model;
  if (file.name.toLowerCase().endsWith('.svg')) return AttachmentType.image;
  return AttachmentType.other;
}

/** PUT the raw File straight to the signed URL. Every file that reaches here
 * was dropped this session and still holds its File (unlike v1's queue, which
 * only has a blob URL), so there's no need to fetch() the bytes back first. */
export async function uploadFileToPresignedUrl(file: File, signedUrl: string) {
  const uploadResponse = await fetch(signedUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      'Cache-Control': CacheControl.IMMUTABLE,
    },
  });
  if (!uploadResponse.ok) {
    throw new Error(
      `Failed to upload ${file.name}: ${uploadResponse.status} ${uploadResponse.statusText}`
    );
  }
}

/** Lean toolData shape the backend validator actually wants for a
 * catalog-picked Endmill row: modelId + laserOrEndmill + bitId, with brandId
 * and spindleSpeed only when present/valid. Deliberately omits inline specs
 * (toolType, vBitDegree, tipRadius) — those trigger the validator's
 * conditional-required rules (e.g. vBitDegree required when toolType is
 * V_BIT) that this flow never collects for. */
type LeanToolData = {
  modelId: string;
  laserOrEndmill: ILaserOrEndmill;
  bitId: string;
  brandId?: string;
  spindleSpeed?: number;
};

/** One row per bit in the group, all anchored on its first model. A row names
 * a single modelId, but a bit applies to the whole group — so writing a row
 * per (model x bit) pair would make PostDetail's ToolsDataCard, which flattens
 * every row into its own tool card, show each bit once per file. Anchoring is
 * how v1 writes them too: 336 of production's 391 groups carry rows for only
 * one of their models. Undefined when no bit has been picked yet, which keeps
 * the group's prior no-toolData behavior. */
/** One bit as the backend stores it. A laser has no spindle, and the backend
 * forbids the field on that row. */
function toolDataRow(bit: V2Bit, modelId: string): LeanToolData {
  const laser = bit.laserOrEndmill === ILaserOrEndmill.LASER;
  const speed = laser ? undefined : validSpindle(bit.spindleSpeed);
  return {
    modelId,
    laserOrEndmill: laser ? ILaserOrEndmill.LASER : ILaserOrEndmill.ENDMILL,
    bitId: bit.bitId,
    ...compact({ brandId: bit.brandId, spindleSpeed: speed }),
  };
}

function buildToolData(
  op: V2Operation,
  modelIds: string[]
): LeanToolData[] | undefined {
  const [anchorModelId] = modelIds;
  const picked = op.bits.filter((bit) => bit.bitId);
  if (!anchorModelId || picked.length === 0) return undefined;

  return picked.map((bit) => toolDataRow(bit, anchorModelId));
}

/** modelGroups.modelIds references attachment _ids, not client-side file
 * ids. toolData is attached only for ops with a catalog bitId selected via
 * the BitPicker — ops still using the parser's free-text bitType guess keep
 * the prior no-toolData behavior (the backend defaults/omits the rest). */
export function buildModelGroups(
  operations: V2Operation[],
  fileIdToAttachmentId: Map<string, string>
) {
  return operations
    .map((op) => {
      const modelIds = op.fileIds
        .map((fileId) => fileIdToAttachmentId.get(fileId))
        .filter((id): id is string => Boolean(id));
      const toolData = buildToolData(op, modelIds);
      return {
        name: op.name,
        modelIds,
        ...(toolData ? { toolData } : {}),
      };
    })
    .filter((group) => group.modelIds.length > 0);
}

/** The v2-file-id -> attachment-id map modelGroups needs, from whatever has
 * been uploaded so far. */
export function fileAttachmentIdMap(files: V2File[]): Map<string, string> {
  const map = new Map<string, string>();
  files.forEach((f) => {
    if (f.attachmentId) map.set(f.id, f.attachmentId);
  });
  return map;
}

type DataError = { data?: { errorMessage?: string; message?: string } };

/** RTK Query errors carry the server's message under `data`, not as an Error. */
function isDataError(error: unknown): error is DataError {
  return !!error && typeof error === 'object' && 'data' in error;
}

function pickDataMessage(data: DataError['data'], fallback: string): string {
  if (!data) return fallback;
  return data.errorMessage || data.message || fallback;
}

export function extractErrorMessage(error: unknown, fallback: string): string {
  if (isDataError(error)) return pickDataMessage(error.data, fallback);
  if (error instanceof Error) return error.message;
  return fallback;
}
